/*
 * Agent write tools. Each one is a dry run first: plan(input, ctx) applies the
 * change to a copy of the group and returns the per-event before/after diff.
 * The registry then asks for confirmation when needed and commits `next`
 * through ctx.commit (one history step, folded into the AI turn).
 */

import insertEvent from "../../../external/cc-lib/dist/functions/eventGroups/insertEvent.js";
import orderByTime from "../../../external/cc-lib/dist/functions/eventGroups/orderByTime.js";
import fixOverlap from "../../../external/cc-lib/dist/functions/eventGroups/fixOverlap.js";
import autoFormat from "../../../external/cc-lib/dist/functions/eventGroups/autoFormat.js";
import correctReadingSpeed from "../../../external/cc-lib/dist/functions/eventGroups/correctReadingSpeed.js";
import EventGroup from "../../../external/cc-lib/dist/classes/eventGroup.js";
import { hasOriginalText } from "../../eventGroupTypes.js";
import { checkMetadataFields, checkWritable, isTime, resolveEvents, resolveGroup } from "./guards.js";
import { markupToHtml, roundTime, toMarkup, toPlain } from "./text.js";

export const MAX_UPDATES_PER_CALL = 500;
export const MAX_CREATES_PER_CALL = 200;
export const MAX_REVIEW_CHANGES = 100;

const COMPARED_FIELDS = ["text", "start", "end", "notes", "approved", "forced", "tags"];
const SETTING_FIELDS = ["name", "type", "language", "rtl", "maxCps", "maxWpm", "maxChars", "maxLines", "minDuration", "maxDuration", "overlap"];

function clone(value) {
    return JSON.parse(JSON.stringify(value));
}

function eventKey(event, index) {
    return event?.id ?? `#${index}`;
}

function snapshot(event) {
    return { text: toPlain(event.text), start: roundTime(event.start), end: roundTime(event.end) };
}

function sameValue(a, b) {
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/**
 * Per-event diff between two versions of a group, matched by event id.
 * @returns {Array<{ type: "updated"|"created"|"deleted", id: string, number: number, before?: object, after?: object }>}
 */
export function diffGroupEvents(before, after) {
    const beforeByKey = new Map();
    (before.events ?? []).forEach((event, index) => beforeByKey.set(eventKey(event, index), { event, index }));
    const seen = new Set();
    const changes = [];
    (after.events ?? []).forEach((event, index) => {
        const key = eventKey(event, index);
        const previous = beforeByKey.get(key);
        seen.add(key);
        if (!previous) {
            changes.push({ type: "created", id: key, number: index + 1, after: snapshot(event) });
        } else if (COMPARED_FIELDS.some((field) => !sameValue(previous.event[field], event[field]))) {
            changes.push({ type: "updated", id: key, number: index + 1, before: snapshot(previous.event), after: snapshot(event) });
        }
    });
    for (const [key, { event, index }] of beforeByKey) {
        if (!seen.has(key)) {
            changes.push({ type: "deleted", id: key, number: index + 1, before: snapshot(event) });
        }
    }
    return changes;
}

function diffSettings(before, after) {
    return SETTING_FIELDS.filter((field) => !sameValue(before[field], after[field])).map((field) => ({
        field,
        before: before[field],
        after: after[field],
    }));
}

/**
 * Shared dry run: resolve and lock-check the group, mutate a copy, diff it.
 * `mutate(draft)` returns { error } or { info } (extra text for the model).
 */
function planGroupEdit(ctx, input, kind, mutate) {
    const resolved = resolveGroup(ctx, input.group);
    if (resolved.error) return { error: resolved.error };
    const { index, group } = resolved;
    const locked = checkWritable(ctx, index);
    if (locked) return { error: locked };

    const draft = clone(group);
    const result = mutate(draft, group) ?? {};
    if (result.error) return { error: result.error };

    const changes = diffGroupEvents(group, draft);
    const settings = diffSettings(group, draft);
    if (changes.some((change) => change.type !== "updated")) {
        // Indexes moved, so the old selection would point at other events.
        draft.selected = [];
    }
    const counts = { updated: 0, created: 0, deleted: 0 };
    changes.forEach((change) => counts[change.type]++);
    return {
        kind,
        groupIndex: index,
        groupId: group.id,
        groupName: group.name,
        next: draft,
        changes,
        settings,
        counts,
        touched: changes.length,
        info: result.info ?? "",
    };
}

function scopeIndexes(group, numbers) {
    if (!numbers) return { indexes: null };
    return resolveEvents(group, numbers);
}

function tagLookup(ctx, group) {
    const byTerm = new Map();
    const add = (tag) => {
        if (tag && typeof tag === "object" && tag.term) byTerm.set(String(tag.term).toLowerCase(), tag);
    };
    (ctx.getTagLibrary?.() ?? []).forEach(add);
    ctx.getGroups().forEach((item) => item.events?.forEach((event) => event.tags?.forEach(add)));
    (group.events ?? []).forEach((event) => event.tags?.forEach(add));
    return (term) => {
        const found = byTerm.get(term.toLowerCase());
        if (found) return clone(found);
        const created = { id: globalThis.crypto.randomUUID(), term, type: "Other", definition: "", color: "info" };
        byTerm.set(term.toLowerCase(), created);
        return created;
    };
}

function updateEvents(input, ctx) {
    if (input.updates.length === 0) return { error: "updates is empty." };
    if (input.updates.length > MAX_UPDATES_PER_CALL) {
        return { error: `At most ${MAX_UPDATES_PER_CALL} updates per call; split the work into several calls.` };
    }
    return planGroupEdit(ctx, input, "update_events", (draft) => {
        const numbers = input.updates.map((update) => update.event);
        const resolved = resolveEvents(draft, numbers);
        if (resolved.error) return { error: resolved.error };
        if (resolved.indexes.length !== numbers.length) return { error: "Each event may appear only once in updates." };
        const fields = new Set(input.updates.flatMap((update) => Object.keys(update)));
        const denied = checkMetadataFields(ctx, fields);
        if (denied) return { error: denied };
        const findTag = fields.has("tags") ? tagLookup(ctx, draft) : null;

        for (const update of input.updates) {
            const event = draft.events[update.event - 1];
            if (update.text !== undefined) {
                const html = markupToHtml(update.text, event.text);
                if (html !== event.text) {
                    event.text = html;
                    event.rendered = false;
                }
            }
            if (update.start !== undefined || update.end !== undefined) {
                const start = update.start ?? event.start;
                const end = update.end ?? event.end;
                if (!isTime(start) || !isTime(end) || end <= start) {
                    return { error: `Event #${update.event}: start and end must be times in seconds with end after start (got ${start} - ${end}).` };
                }
                event.start = start;
                event.end = end;
            }
            if (update.notes !== undefined) event.notes = update.notes;
            if (update.approved !== undefined) event.approved = update.approved;
            if (update.forced !== undefined) event.forced = update.forced;
            if (update.tags !== undefined) {
                const terms = [...new Set(update.tags.map((tag) => tag.trim()).filter(Boolean))];
                event.tags = terms.map(findTag);
            }
        }
        const textNumbers = input.updates.filter((update) => update.text !== undefined).map((update) => update.event);
        const info = [lineLimitWarning(draft, textNumbers), textNumbers.length ? replyLanguageReminder(ctx, draft) : ""];
        return { info: info.filter(Boolean).join(" ") };
    });
}

/**
 * After writing subtitles in another language the model tends to keep
 * replying in it; the tool result is the last thing it reads before replying.
 */
function replyLanguageReminder(ctx, group) {
    const ui = String(ctx.uiLanguage ?? "").slice(0, 2).toLowerCase();
    const written = String(group.language ?? "").slice(0, 2).toLowerCase();
    if (!ui || !written || ui === written) return "";
    return `Reminder: the text you wrote is ${group.language}, but write your reply to the user in the language of their message (interface language: ${ctx.uiLanguage}).`;
}

/**
 * Events (1-based numbers) whose new text breaks the group's line limits, as
 * a note for the model to fix in its next call (translations often run long).
 */
function lineLimitWarning(group, numbers) {
    const maxChars = group.maxChars || 0;
    const maxLines = group.maxLines || 0;
    if (!maxChars && !maxLines) return "";
    const over = numbers.filter((number) => {
        const lines = toPlain(group.events[number - 1]?.text).split("\n").filter((line) => line.trim());
        return (maxLines && lines.length > maxLines) || (maxChars && lines.some((line) => line.length > maxChars));
    });
    if (!over.length) return "";
    const list = over.slice(0, 30).map((number) => `#${number}`).join(", ");
    return `Warning: ${over.length} event(s) now break the group's limit of ${maxLines} lines of ${maxChars} characters: ${list}${over.length > 30 ? ", …" : ""}. Re-break or condense them (keeping the meaning) with another update_events call.`;
}

function createEvents(input, ctx) {
    if (input.events.length === 0) return { error: "events is empty." };
    if (input.events.length > MAX_CREATES_PER_CALL) {
        return { error: `At most ${MAX_CREATES_PER_CALL} events per call; split the work into several calls.` };
    }
    for (const [i, item] of input.events.entries()) {
        if (!isTime(item.start) || !isTime(item.end) || item.end <= item.start) {
            return { error: `events[${i}]: start and end must be times in seconds with end after start.` };
        }
    }
    return planGroupEdit(ctx, input, "create_events", (draft) => {
        for (const item of input.events) {
            insertEvent(draft, { start: item.start, end: item.end, text: markupToHtml(item.text) });
        }
        orderByTime(draft);
        return { info: "Events were inserted in time order; overlapping times may have been adjusted to fit." };
    });
}

function deleteEvents(input, ctx) {
    if (input.events.length === 0) return { error: "events is empty." };
    return planGroupEdit(ctx, input, "delete_events", (draft) => {
        const resolved = resolveEvents(draft, input.events);
        if (resolved.error) return { error: resolved.error };
        const remove = new Set(resolved.indexes);
        draft.events = draft.events.filter((_, index) => !remove.has(index));
        return {};
    });
}

function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function searchReplace(input, ctx) {
    if (!input.find) return { error: "find must not be empty." };
    let pattern;
    try {
        const source = input.regex ? input.find : escapeRegExp(input.find);
        const flags = input.case_sensitive ? "gu" : "giu";
        pattern = input.whole_word
            ? new RegExp(`(?<![\\p{L}\\p{N}_])(?:${source})(?![\\p{L}\\p{N}_])`, flags)
            : new RegExp(source, flags);
    } catch (error) {
        return { error: `Invalid regular expression: ${error.message}` };
    }
    return planGroupEdit(ctx, input, "search_replace", (draft) => {
        const scope = scopeIndexes(draft, input.events);
        if (scope.error) return { error: scope.error };
        const targets = scope.indexes ? new Set(scope.indexes) : null;
        draft.events.forEach((event, index) => {
            if (targets && !targets.has(index)) return;
            // Replace in the visible text, never inside the HTML markup.
            const markup = toMarkup(event.text);
            const replaced = markup.replace(pattern, input.replace);
            if (replaced !== markup) {
                event.text = markupToHtml(replaced, event.text);
                event.rendered = false;
            }
        });
        return {};
    });
}

function shiftTiming(input, ctx) {
    const offset = input.offset ?? 0;
    if (!offset && !input.fix_overlaps && !input.extend_to_min_duration) {
        return { error: "Nothing to do: give an offset, fix_overlaps or extend_to_min_duration." };
    }
    const project = ctx.getProject();
    return planGroupEdit(ctx, input, "shift_timing", (draft) => {
        const scope = scopeIndexes(draft, input.events);
        if (scope.error) return { error: scope.error };
        const targetIds = new Set(
            (scope.indexes ?? draft.events.map((_, i) => i)).map((i) => eventKey(draft.events[i], i)),
        );
        const isTarget = (event, index) => targetIds.has(eventKey(event, index));
        const notes = [];

        if (offset) {
            let clamped = 0;
            draft.events.forEach((event, index) => {
                if (!isTarget(event, index) || !isTime(event.start) || !isTime(event.end)) return;
                const duration = event.end - event.start;
                const start = event.start + offset;
                if (start < 0) clamped++;
                event.start = Math.max(0, start);
                event.end = event.start + duration;
            });
            if (clamped) notes.push(`${clamped} event(s) would start before 0 and were placed at 0.`);
        }
        orderByTime(draft);

        if (input.extend_to_min_duration) {
            const minDuration = draft.minDuration || 0;
            const gap = (ctx.minFrameGap || 0) / Math.round(project.frameRate || 30);
            draft.events.forEach((event, index) => {
                if (!isTarget(event, index) || !isTime(event.start) || !isTime(event.end)) return;
                if (event.end - event.start >= minDuration) return;
                const next = draft.events[index + 1];
                const limit = next && isTime(next.start) ? next.start - gap : Infinity;
                event.end = Math.max(event.end, Math.min(event.start + minDuration, limit));
            });
        }
        if (input.fix_overlaps) {
            fixOverlap(draft, ctx.minFrameGap || 0, project.frameRate);
            notes.push("Overlaps were fixed across the whole group.");
        }
        return { info: notes.join(" ") };
    });
}

function validateSettings(input) {
    const range = (key, min, max) => {
        const value = input[key];
        if (value !== undefined && !(value >= min && value <= max)) return `${key} must be between ${min} and ${max}.`;
        return null;
    };
    const error =
        range("maxCps", 1, 100) ||
        range("maxWpm", 1, 1000) ||
        range("maxChars", 1, 200) ||
        range("maxLines", 1, 20) ||
        range("minDuration", 0, 60) ||
        range("maxDuration", 0.1, 600);
    if (error) return error;
    if (input.name !== undefined && (!input.name.trim() || input.name.length > 120)) return "name must be 1-120 characters.";
    if (input.language !== undefined && !/^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/.test(input.language)) {
        return "language must be a BCP 47 code such as en-US.";
    }
    return null;
}

function setGroupSettings(input, ctx) {
    const keys = Object.keys(input).filter((key) => key !== "group");
    if (!keys.length) return { error: "No settings given." };
    const invalid = validateSettings(input);
    if (invalid) return { error: invalid };
    return planGroupEdit(ctx, input, "set_group_settings", (draft) => {
        for (const key of keys) {
            draft[key] = key === "name" ? input.name.trim() : input[key];
        }
        if (draft.minDuration > draft.maxDuration) return { error: "minDuration must not exceed maxDuration." };
        return {};
    });
}

function autoFormatTool(input, ctx) {
    return planGroupEdit(ctx, input, "auto_format", (draft) => {
        const scope = scopeIndexes(draft, input.events);
        if (scope.error) return { error: scope.error };
        autoFormat(draft, draft.maxLines || 2, draft.maxChars || 32, draft.minDuration || 0, true, scope.indexes);
        return {};
    });
}

function correctReadingSpeedTool(input, ctx) {
    const project = ctx.getProject();
    return planGroupEdit(ctx, input, "correct_reading_speed", (draft) => {
        const scope = scopeIndexes(draft, input.events);
        if (scope.error) return { error: scope.error };
        const { stats } = correctReadingSpeed(draft, {
            frameRate: project.frameRate,
            maxCps: draft.maxCps,
            minDuration: draft.minDuration,
            maxDuration: draft.maxDuration,
            minFrameGap: 2,
            allowMerge: input.allow_merge === true,
            maxChars: draft.maxChars,
            maxLines: draft.maxLines,
            excludeSpaces: ctx.excludeSpaces,
            markFailures: true,
            failureNote: ctx.failureNote,
            targetIds: scope.indexes ? scope.indexes.map((i) => draft.events[i].id) : null,
        });
        return {
            info: `Reading speed: ${stats.extended} fixed by retiming, ${stats.merged} merged, ${stats.failed} could not be fixed (marked unapproved with a note).`,
        };
    });
}

const CREATABLE_TYPES = ["subtitle", "translation", "transcription"];
/** Settings a new group takes from its source group. */
const COPIED_GROUP_SETTINGS = [
    "incode", "frameRate", "dropFrame", "fontSize", "fontFamily", "originalFontFamily", "overlap",
    "maxCps", "maxWpm", "maxChars", "maxLines", "displayWidth", "displayHeight", "minDuration", "maxDuration", "illegalChars",
];
const RTL_LANGUAGES = /^(ar|he|iw|fa|ur|yi|ps|sd|ug|dv)\b/i;

/**
 * New Event Group, optionally built from another group's timings. A
 * translation group is linked to its source and keeps the source text in
 * each event's `original`, which the editor shows beside the translation.
 * The group is appended; its text is filled with update_events afterwards.
 */
function createGroup(input, ctx) {
    const name = String(input.name ?? "").trim();
    if (!name || name.length > 120) return { error: "name must be 1-120 characters." };
    if (!/^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/.test(input.language)) {
        return { error: "language must be a BCP 47 code such as fr-FR." };
    }
    let source = null;
    if (input.from_group !== undefined) {
        const resolved = resolveGroup(ctx, input.from_group);
        if (resolved.error) return { error: resolved.error };
        source = resolved.group;
    }
    if (input.copy && !source) return { error: "copy needs from_group." };
    const type = input.type ?? (source ? "translation" : "subtitle");
    if (!CREATABLE_TYPES.includes(type)) return { error: `type must be one of ${CREATABLE_TYPES.join(", ")}.` };
    const copyText = input.copy === "timing_and_text";
    const keepsOriginal = hasOriginalText(type);

    const options = { name, type, language: input.language, rtl: RTL_LANGUAGES.test(input.language) };
    for (const key of COPIED_GROUP_SETTINGS) {
        if (source?.[key] !== undefined) options[key] = clone(source[key]);
    }
    const group = clone({ ...new EventGroup(options) });
    group.id = globalThis.crypto.randomUUID();
    group.selected = [];
    group.locked = false;
    if (source) {
        group.originalRtl = Boolean(source.rtl);
        if (keepsOriginal) group.linkedGroup = source.id;
        group.events = (source.events ?? []).map((event) => ({
            ...clone(event),
            id: globalThis.crypto.randomUUID(),
            original: keepsOriginal ? event.text : event.original ?? "",
            text: copyText ? event.text : "",
            notes: "",
            reply: "",
            approved: undefined,
            rendered: false,
            audioRenderHash: "",
            audioFile: "",
            audioFileUuid: false,
            audioFileRemote: "",
            waveformFile: "",
            peaksData: [],
        }));
    }

    const groupIndex = ctx.getGroups().length;
    const changes = group.events.map((event, index) => ({ type: "created", id: event.id, number: index + 1, after: snapshot(event) }));
    const from = source
        ? ` from group ${input.from_group} with its ${group.events.length} event timings${keepsOriginal ? ", the source text kept as each event's original" : ""}`
        : "";
    const next = group.events.length && !copyText
        ? ` Its text is empty: fill it with update_events on group ${groupIndex + 1} (about 100 events per call), then select it with select_group.`
        : "";
    return {
        kind: "create_group",
        create: true,
        groupIndex,
        groupId: group.id,
        groupName: name,
        next: group,
        changes,
        settings: [{ field: "group", before: null, after: `${name} (${type}, ${input.language})` }],
        counts: { updated: 0, created: changes.length, deleted: 0 },
        touched: changes.length,
        info: [`Created group ${groupIndex + 1} "${name}"${from}.${next}`, replyLanguageReminder(ctx, group)].filter(Boolean).join(" "),
    };
}

export const WRITE_TOOLS = {
    create_group: createGroup,
    update_events: updateEvents,
    create_events: createEvents,
    delete_events: deleteEvents,
    search_replace: searchReplace,
    shift_timing: shiftTiming,
    set_group_settings: setGroupSettings,
    auto_format: autoFormatTool,
    correct_reading_speed: correctReadingSpeedTool,
};
